import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  preRegistrationsApi,
  type PreRegistrationStatus,
} from "../api/preRegistrations";
import { qk } from "../../../../lib/queryKeys";
import { useClientScope } from "../../../../shared/contexts";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const PreRegistrations: React.FC = () => {
  const queryClient = useQueryClient();
  const { isCentralAdmin, effectiveClientId } = useClientScope();
  const [answersOpen, setAnswersOpen] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState<string | null>(null);
  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [activeStatusTab, setActiveStatusTab] =
    React.useState<PreRegistrationStatus>("pending");
  const [actionSummary, setActionSummary] = React.useState<{
    severity: "success" | "warning";
    message: string;
  } | null>(null);

  const listQuery = useQuery({
    queryKey: qk.preRegistrations(effectiveClientId ?? null, activeStatusTab),
    queryFn: () => preRegistrationsApi.list(effectiveClientId, activeStatusTab),
  });

  const entries = listQuery.data?.data ?? [];

  React.useEffect(() => {
    setSelectedEmails((current) =>
      current.filter((email) => entries.some((entry) => entry.email === email))
    );
  }, [entries]);

  React.useEffect(() => {
    setSelectedEmails([]);
  }, [activeStatusTab]);

  const answersQuery = useQuery({
    queryKey: ["pre-registration-answers", selectedEmail],
    queryFn: () =>
      selectedEmail ? preRegistrationsApi.getAnswers(selectedEmail) : null,
    enabled: Boolean(selectedEmail),
  });

  const approveMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const failed: string[] = [];
      let approved = 0;

      for (const email of emails) {
        try {
          await preRegistrationsApi.approve(email);
          approved += 1;
        } catch (_error) {
          failed.push(email);
        }
      }

      return { approved, failed };
    },
    onSuccess: (result) => {
      setActionSummary({
        severity: result.failed.length ? "warning" : "success",
        message: `Approved ${result.approved} registration${
          result.approved === 1 ? "" : "s"
        }${result.failed.length ? `. Failed: ${result.failed.join(", ")}` : "."}`,
      });
      queryClient.invalidateQueries({
        queryKey: ["pre-registrations"],
      });
      setSelectedEmails([]);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (email: string) => preRegistrationsApi.reject(email),
    onSuccess: (result, email) => {
      setActionSummary({
        severity: result.email_sent === false ? "warning" : "success",
        message:
          result.email_sent === false
            ? `Rejected ${email}, but email failed to send: ${
                result.email_error || "Unknown error"
              }`
            : `Rejected ${email}.`,
      });
      queryClient.invalidateQueries({
        queryKey: ["pre-registrations"],
      });
      setSelectedEmails((current) => current.filter((item) => item !== email));
    },
    onError: (_error, email) => {
      setActionSummary({
        severity: "warning",
        message: `Failed to reject ${email}.`,
      });
    },
  });

  const allSelected = entries.length > 0 && selectedEmails.length === entries.length;
  const hasSelection = selectedEmails.length > 0;

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Pre-Registrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review registrations, reject if needed, and later approve from the rejected tab.
          </Typography>
          <Tabs
            value={activeStatusTab}
            onChange={(_event, nextValue) => setActiveStatusTab(nextValue)}
            sx={{ mt: 2, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Tab value="pending" label="Pending" sx={{ textTransform: "none" }} />
            <Tab value="rejected" label="Rejected" sx={{ textTransform: "none" }} />
          </Tabs>
          {!isCentralAdmin && !effectiveClientId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No client assigned to your account.
            </Alert>
          )}
          {actionSummary && (
            <Alert
              severity={actionSummary.severity}
              sx={{ mt: 2 }}
            >
              {actionSummary.message}
            </Alert>
          )}
        </Box>

        {listQuery.isLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading pre-registrations...</Typography>
          </Box>
        ) : listQuery.isError ? (
          <Alert severity="error">Failed to load pre-registrations.</Alert>
        ) : entries.length ? (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              <Button
                variant="contained"
                disabled={!hasSelection || approveMutation.isPending}
                onClick={() => {
                  setActionSummary(null);
                  approveMutation.mutate(selectedEmails);
                }}
              >
                {approveMutation.isPending
                  ? "Approving..."
                  : `Approve Selected (${selectedEmails.length})`}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {activeStatusTab === "pending"
                  ? "Select one or more registrations to bulk approve."
                  : "Select rejected registrations to approve them later."}
              </Typography>
            </Stack>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={hasSelection && !allSelected}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedEmails(entries.map((entry) => entry.email));
                        } else {
                          setSelectedEmails([]);
                        }
                      }}
                      inputProps={{ "aria-label": "select all pre-registrations" }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const checked = selectedEmails.includes(entry.email);
                  return (
                    <TableRow key={entry.email} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checked}
                          onChange={() =>
                            setSelectedEmails((current) =>
                              current.includes(entry.email)
                                ? current.filter((email) => email !== entry.email)
                                : [...current, entry.email]
                            )
                          }
                          inputProps={{ "aria-label": `select ${entry.email}` }}
                        />
                      </TableCell>
                      <TableCell>{entry.name || "-"}</TableCell>
                      <TableCell>{entry.email}</TableCell>
                      <TableCell>{entry.client_name || entry.client_id || "-"}</TableCell>
                      <TableCell>{formatDateTime(entry.submitted_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSelectedEmail(entry.email);
                              setAnswersOpen(true);
                            }}
                          >
                            View answers
                          </Button>
                          {activeStatusTab === "pending" && (
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              disabled={rejectMutation.isPending}
                              onClick={() => {
                                setActionSummary(null);
                                rejectMutation.mutate(entry.email);
                              }}
                            >
                              {rejectMutation.isPending ? "Rejecting..." : "Reject"}
                            </Button>
                          )}
                          {activeStatusTab === "rejected" && (
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={approveMutation.isPending}
                              onClick={() => {
                                setActionSummary(null);
                                approveMutation.mutate([entry.email]);
                              }}
                            >
                              {approveMutation.isPending ? "Approving..." : "Approve"}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        ) : (
          <Alert severity="info">
            {activeStatusTab === "pending"
              ? "No pending pre-registrations."
              : "No rejected pre-registrations."}
          </Alert>
        )}
      </Stack>

      <Dialog
        open={answersOpen}
        onClose={() => {
          setAnswersOpen(false);
          setSelectedEmail(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pre-registration answers</DialogTitle>
        <DialogContent>
          {answersQuery.isLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading answers...</Typography>
            </Box>
          ) : answersQuery.isError ? (
            <Alert severity="error">Failed to load answers.</Alert>
          ) : answersQuery.data?.data ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Email: {answersQuery.data.data.email}
              </Typography>
              {answersQuery.data.data.client_name && (
                <Typography variant="body2" color="text.secondary">
                  Client: {answersQuery.data.data.client_name}
                </Typography>
              )}
              <Stack spacing={1.5}>
                {answersQuery.data.data.answers.map((answer) => {
                  const value = Array.isArray(answer.answer)
                    ? answer.answer.join(", ")
                    : answer.answer ?? "-";
                  return (
                    <Box key={answer.question_id}>
                      <Typography variant="subtitle2">
                        {answer.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {String(value)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No answers found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
